import React, { useState, useEffect } from 'react';
import {
  CheckOutlined,
  CloseOutlined,
  StarFilled,
  MailOutlined,
  ArrowRightOutlined,
  RightOutlined,
} from '@ant-design/icons';
import ModalContainer from '../modals/ModalContainer';
import GmailMark from './GmailMark';
import { getGmailAddonUrl } from '../../utils/replyCaptureStorage';
import { captureEvent } from '../../services/analytics';
import css from './ReplyCaptureModal.module.scss';

type ModalVariant = 'full' | 'compact';

interface ReplyCaptureModalProps {
  open: boolean;
  variant?: ModalVariant;
  recipientFirstName?: string;
  applicationCount?: number;
  onInstallAddon: () => void;
  onSetupForward: () => void;
  onLater: () => void;
  onClose: () => void;
}

const ReplyCaptureModal: React.FC<ReplyCaptureModalProps> = ({
  open,
  variant = 'full',
  recipientFirstName,
  applicationCount,
  onInstallAddon,
  onSetupForward,
  onLater,
  onClose,
}) => {
  const [forwardExpanded, setForwardExpanded] = useState(false);
  const [otherOptionsOpen, setOtherOptionsOpen] = useState(variant === 'full');

  useEffect(() => {
    if (open) {
      captureEvent('reply_capture_prompt_shown', {
        variant,
        application_count: applicationCount ?? null,
      });
    }
  }, [open, variant, applicationCount]);

  const dismiss = (via: string) => {
    captureEvent('reply_capture_prompt_dismissed', { variant, dismissed_via: via });
  };

  const handleInstall = () => {
    captureEvent('email_addon_install_clicked', { variant });
    window.open(getGmailAddonUrl(), '_blank', 'noopener');
    onInstallAddon();
  };

  const isCompact = variant === 'compact';
  const headingText = isCompact
    ? `That's ${applicationCount ?? 'a few'} sent — and counting`
    : 'Your application is on its way';

  const replyClause = recipientFirstName ? `${recipientFirstName} replies` : 'they reply';
  const copyText = isCompact
    ? `Replies are coming — Gmail threads with your name on them. Want Job Beast to track them automatically?`
    : `When ${replyClause}, the message lands in your Gmail. Want Job Beast to track replies for you, automatically?`;

  return (
    <ModalContainer isOpen={open} onClose={() => { dismiss('backdrop'); onClose(); }}>
      <div className={`${css.modal} ${isCompact ? css.modalCompact : ''}`}>
        <button className={css.dismiss} onClick={() => { dismiss('close_button'); onClose(); }} aria-label="Close">
          <CloseOutlined />
        </button>

        {!isCompact && (
          <div className={css.celebration} aria-hidden="true">
            <span className={css.checkRing}>
              <CheckOutlined className={css.check} />
            </span>
          </div>
        )}

        <h2 className={css.heading}>{headingText}</h2>
        <p className={css.copy}>{copyText}</p>

        <article className={css.recommended}>
          <span className={css.recommendedPill}>
            <StarFilled /> Recommended
          </span>

          <div className={css.recommendedBody}>
            <div className={css.recommendedIcon} aria-hidden="true">
              <GmailMark width={28} height={22} />
            </div>
            <div className={css.recommendedText}>
              <h3>Install the Gmail add-on</h3>
              <p>
                See application context inside Gmail. We only read the email
                you have open — your inbox stays private.
              </p>
            </div>
          </div>

          <button className={css.primary} onClick={handleInstall}>
            Install add-on <ArrowRightOutlined className={css.arrow} />
          </button>
        </article>

        {isCompact && !otherOptionsOpen ? (
          <button
            className={css.otherToggle}
            onClick={() => setOtherOptionsOpen(true)}
          >
            Other ways to capture replies <ArrowRightOutlined className={css.otherArrow} />
          </button>
        ) : (
          <>
            <p className={css.altLabel}>Prefer to forward replies yourself?</p>

            <button
              className={`${css.altRow} ${forwardExpanded ? css.altRowOpen : ''}`}
              onClick={() => setForwardExpanded((v) => !v)}
              aria-expanded={forwardExpanded}
            >
              <MailOutlined className={css.altIcon} />
              <div className={css.altText}>
                <span className={css.altTitle}>Forward replies manually</span>
                <span className={css.altSubtitle}>No setup. You're in full control.</span>
              </div>
              <RightOutlined className={css.altChevron} />
            </button>
            {forwardExpanded && (
              <div className={css.altDetail}>
                <p>
                  When you get a reply, forward it to your Job Beast address.
                  We match it to the right application automatically.
                </p>
                <button className={css.altCta} onClick={onSetupForward}>
                  Show me my address →
                </button>
              </div>
            )}
          </>
        )}

        <button className={css.later} onClick={() => { dismiss('maybe_later'); onLater(); }}>
          Maybe later
        </button>
      </div>
    </ModalContainer>
  );
};

export default ReplyCaptureModal;
